/**
 * Simple logger service implementation
 * Only outputs console.log in development mode for debugging purposes
 * In production mode, all logging calls are silently ignored with zero overhead
 */
export class LoggerService {
    constructor() {
        Object.defineProperty(this, "isDevelopment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Check if we're in development mode
        this.isDevelopment = !import.meta.env.PROD;
    }
    static getInstance() {
        if (!LoggerService.instance) {
            LoggerService.instance = new LoggerService();
        }
        return LoggerService.instance;
    }
    /**
     * Debug logging - only shows in development mode
     * Simple console.log output for debugging purposes
     * In production, this becomes a no-op for maximum performance
     */
    debug(...args) {
        if (this.isDevelopment) {
            const caller = this.getCallerInfo();
            if (caller) {
                // eslint-disable-next-line no-console -- LoggerService is the canonical console.* wrapper (R47)
                console.log(`[${caller}]`, ...args);
            }
            else {
                // eslint-disable-next-line no-console -- LoggerService is the canonical console.* wrapper (R47)
                console.log(...args);
            }
        }
        // In production: this.isDevelopment is false, so this becomes a no-op
        // The JavaScript engine can optimize away the entire function call
    }
    /**
     * Error logging — required by R47 for silent catches. Always emitted (including in
     * production) so the failure surfaces in browser DevTools and any console-scraping
     * observability tooling. Goes through `console.error` to get the red-flag styling +
     * stack capture browsers apply.
     */
    error(...args) {
        const caller = this.getCallerInfo();
        if (caller) {
            // eslint-disable-next-line no-console -- LoggerService is the canonical console.* wrapper (R47)
            console.error(`[${caller}]`, ...args);
        }
        else {
            // eslint-disable-next-line no-console -- LoggerService is the canonical console.* wrapper (R47)
            console.error(...args);
        }
    }
    /**
     * Extract caller information from the stack trace
     * Returns a formatted string with file name and line number
     */
    getCallerInfo() {
        try {
            const stack = new Error().stack;
            if (!stack)
                return null;
            // Stack trace format (Chrome/Edge):
            // Error
            //   at LoggerService.getCallerInfo (...)
            //   at LoggerService.debug (...)
            //   at actualCaller (...)  <- We want this line
            const lines = stack.split("\n");
            // Skip the first 3 lines (Error, getCallerInfo, debug)
            // The 4th line is the actual caller
            if (lines.length > 3) {
                const callerLine = lines[3].trim();
                // Extract function name and location
                // Format: "at functionName (file:line:column)" or "at file:line:column"
                const match = callerLine.match(/at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?$/);
                if (match) {
                    const functionName = match[1] || "anonymous";
                    const filePath = match[2];
                    const lineNumber = match[3];
                    // Extract just the filename from the full path
                    const fileName = filePath.split("/").pop()?.split("\\").pop() || filePath;
                    return `${functionName} @ ${fileName}:${lineNumber}`;
                }
            }
            return null;
        }
        catch (error) {
            // If stack trace parsing fails, just return null
            return null;
        }
    }
}
// Export a singleton instance
export const logger = LoggerService.getInstance();
